import { useEffect, useRef } from "react";
import { toast } from "sonner";
import type { UpdateProgress } from "../../../types/electron";

/**
 * Hook to listen for Electron auto-updater events and show toast notifications
 *
 * Only active when running in Electron (desktop app).
 * Shows non-intrusive toast notifications for:
 * - Update checking started
 * - No updates available (manual check only)
 * - Download progress
 * - Update errors
 *
 * Critical update decisions (update available, update downloaded) use native
 * Electron dialogs for better visibility and user confirmation.
 */
export function useAutoUpdaterNotifications() {
  const downloadToastIdRef = useRef<string | number | undefined>(undefined);

  useEffect(() => {
    if (!globalThis.electronAPI) {
      return; // Not running in Electron
    }

    // Show toast when checking for updates
    globalThis.electronAPI.onUpdateChecking(() => {
      toast.info("Checking for updates...", {
        duration: 2000,
      });
    });

    // Show toast when no updates available (typically from manual check)
    globalThis.electronAPI.onUpdateNotAvailable(() => {
      toast.success("You're running the latest version", {
        duration: 3000,
      });
    });

    // Show progress toast during download
    globalThis.electronAPI.onUpdateDownloadProgress((progress: UpdateProgress) => {
      const rounded = Math.round(progress.percent);
      const mbTransferred = (progress.transferred / 1024 / 1024).toFixed(1);
      const mbTotal = (progress.total / 1024 / 1024).toFixed(1);

      if (downloadToastIdRef.current) {
        // Update existing toast
        toast.loading(`Downloading update... ${rounded}% (${mbTransferred}/${mbTotal} MB)`, {
          id: downloadToastIdRef.current,
          duration: Infinity,
        });
      } else {
        // Create initial toast
        downloadToastIdRef.current = toast.loading(`Downloading update... ${rounded}%`, {
          duration: Infinity,
        });
      }

      // Clear toast when complete
      if (rounded >= 100) {
        setTimeout(() => {
          if (downloadToastIdRef.current) {
            toast.dismiss(downloadToastIdRef.current);
            downloadToastIdRef.current = undefined;
          }
        }, 1000);
      }
    });

    // Show error toast if update check fails
    globalThis.electronAPI.onUpdateError((message: string) => {
      toast.error("Update check failed", {
        description: message,
        duration: 5000,
      });
    });
  }, []);
}
